import {
  attributeKeys,
  type ContextAttributes,
} from "@/utils/context/attributes";
import { notNull } from "@/utils/helpers";
import type { BaggageEntries } from "@listen-labs/o11y/tracing";

// What crosses a process boundary as a W3C `baggage` header. Every context
// attribute propagates today; a new attribute stays in-process until it is
// listed here.
const propagatedKeys = [
  "studyId",
  "organizationId",
  "userId",
  "responseId",
] as const satisfies readonly (keyof ContextAttributes)[];

export type TraceBaggage = Pick<
  ContextAttributes,
  (typeof propagatedKeys)[number]
>;

const isPropagated = (key: keyof ContextAttributes): boolean =>
  propagatedKeys.some((propagated) => propagated === key);

const baggageKeys = attributeKeys.filter(([key]) => isPropagated(key));

export const baggageNames = baggageKeys.map(([, name]) => name);

export const toBaggageEntries = (
  attributes: ContextAttributes
): BaggageEntries =>
  Object.fromEntries(
    baggageKeys.flatMap(([key, name]) => {
      const value = attributes[key];
      return notNull(value) ? [[name, value]] : [];
    })
  );

export const fromBaggageEntries = (entries: BaggageEntries): TraceBaggage =>
  baggageKeys.reduce<TraceBaggage>((acc, [key, name]) => {
    const value = entries[name];
    return notNull(value) ? { ...acc, [key]: value } : acc;
  }, {});
